import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/api';
import { toast } from 'sonner';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Shield, FileText, Upload, CheckCircle2, XCircle, Clock, AlertTriangle,
  ChevronDown, ChevronRight, Search, Filter, Plus, Eye, Trash2,
  MapPin, ArrowLeft, RefreshCw, History, Building2, Lock,
  FolderOpen, Calendar, HardHat, Gavel, FileCheck, ClipboardList,
  Package, Settings, Ban, Download
} from 'lucide-react';
import FileUploadZone from '@/components/FileUploadZone';
import ContractExtractor from '@/components/ContractExtractor';

const CATEGORY_META = {
  contract_documents: { label: 'Contract Documents', icon: Gavel, color: 'text-blue-600', bg: 'bg-blue-50' },
  programme: { label: 'Programme Documents', icon: Calendar, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  security_insurance: { label: 'Security / Insurance', icon: Shield, color: 'text-green-600', bg: 'bg-green-50' },
  access_possession: { label: 'Access / Possession', icon: MapPin, color: 'text-amber-600', bg: 'bg-amber-50' },
  drawings_technical: { label: 'Drawings / Technical', icon: FileText, color: 'text-cyan-600', bg: 'bg-cyan-50' },
  compliance_statutory: { label: 'Compliance / Statutory', icon: HardHat, color: 'text-red-600', bg: 'bg-red-50' },
  commercial_admin: { label: 'Commercial / Admin', icon: Building2, color: 'text-purple-600', bg: 'bg-purple-50' },
  procedures_communication: { label: 'Procedures / Communication', icon: ClipboardList, color: 'text-slate-600', bg: 'bg-slate-50' },
  procurement_design: { label: 'Procurement / Design', icon: Package, color: 'text-orange-600', bg: 'bg-orange-50' },
};

const STATUS_BADGE = {
  missing: { label: 'Missing', className: 'bg-red-100 text-red-700 border-red-200' },
  pending_upload: { label: 'Pending Upload', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  uploaded: { label: 'Uploaded', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  under_review: { label: 'Under Review', className: 'bg-purple-100 text-purple-700 border-purple-200' },
  verified: { label: 'Verified', className: 'bg-green-100 text-green-700 border-green-200' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-200' },
  superseded: { label: 'Superseded', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  expired: { label: 'Expired', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  not_applicable: { label: 'N/A', className: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const REQ_BADGE = {
  mandatory: { label: 'Mandatory', className: 'bg-red-50 text-red-600 border-red-200' },
  conditional: { label: 'Conditional', className: 'bg-amber-50 text-amber-600 border-amber-200' },
  optional: { label: 'Optional', className: 'bg-slate-50 text-slate-500 border-slate-200' },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.missing;
  return <Badge variant="outline" className={`text-[10px] font-semibold ${s.className}`}>{s.label}</Badge>;
}

function ReqBadge({ type }) {
  const r = REQ_BADGE[type] || REQ_BADGE.optional;
  return <Badge variant="outline" className={`text-[10px] ${r.className}`}>{r.label}</Badge>;
}

// Readiness Panel
function ReadinessPanel({ readiness, onRefresh }) {
  if (!readiness) return null;
  const pct = readiness.readiness_percentage;
  const barColor = pct === 100 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const headerBg = pct === 100 ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300';

  return (
    <Card className={`border-2 ${pct === 100 ? 'border-green-300' : 'border-amber-300'} shadow-sm mb-6`} data-testid="readiness-panel">
      <CardContent className="p-0">
        <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-5 ${headerBg} rounded-t-lg gap-3`}>
          <div className="flex items-center gap-3">
            <Shield className={`h-5 w-5 ${pct === 100 ? 'text-green-600' : 'text-amber-600'}`} />
            <div>
              <h3 className="font-bold text-sm sm:text-base text-slate-900">Project Setup Readiness</h3>
              <p className={`text-xs ${pct === 100 ? 'text-green-700' : 'text-amber-700'}`}>
                {readiness.completed_required} of {readiness.total_required} required items complete &middot; {readiness.status_label}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-lg font-black ${pct === 100 ? 'text-green-600' : 'text-amber-600'}`}>{pct}%</span>
            <Button variant="ghost" size="sm" onClick={onRefresh} className="h-7 w-7 p-0">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <div className="h-1.5 bg-slate-200">
          <div className={`h-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
        {readiness.missing_items.length > 0 && (
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-red-700">
                  {readiness.missing_items.length} required document{readiness.missing_items.length > 1 ? 's' : ''} missing
                  {readiness.missing_setup_fields?.length > 0 && ` · ${readiness.missing_setup_fields.length} setup field${readiness.missing_setup_fields.length > 1 ? 's' : ''} incomplete`}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {readiness.missing_items.slice(0, 8).map((m, i) => (
                    <span key={i} className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded border border-red-200">
                      {m.title}
                    </span>
                  ))}
                  {readiness.missing_items.length > 8 && (
                    <span className="text-[10px] text-red-500">+{readiness.missing_items.length - 8} more</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Document Row
function DocumentRow({ doc, onAction }) {
  const catMeta = CATEGORY_META[doc.document_category] || { label: doc.document_category, icon: FileText, color: 'text-slate-600' };
  const status = doc.current_status;
  const isMissing = status === 'missing' || status === 'pending_upload';
  const isVerified = status === 'verified';
  const isNA = status === 'not_applicable';

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${isNA ? 'opacity-50' : ''}`}
      data-testid={`doc-row-${doc.id}`}
    >
      <div className={`w-1.5 h-8 rounded-full flex-shrink-0 ${
        isVerified ? 'bg-green-400' : isMissing ? 'bg-red-400' : isNA ? 'bg-slate-300' : 'bg-amber-400'
      }`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900 truncate">{doc.title}</span>
          <ReqBadge type={doc.requirement_type} />
          <StatusBadge status={status} />
        </div>
        {doc.conditional_rule_reference && (
          <p className="text-[10px] text-amber-600 mt-0.5">{doc.conditional_rule_reference}</p>
        )}
        {doc.file_name && (
          <p className="text-[10px] text-slate-500 mt-0.5 truncate">{doc.file_name} {doc.uploaded_at ? `· ${new Date(doc.uploaded_at).toLocaleDateString()}` : ''}</p>
        )}
        {doc.rejection_reason && status === 'rejected' && (
          <p className="text-[10px] text-red-500 mt-0.5">Rejected: {doc.rejection_reason}</p>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {isMissing && (
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => onAction('upload', doc)}>
            <Upload className="h-3 w-3" /> Upload
          </Button>
        )}
        {status === 'uploaded' && (
          <>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-green-600 border-green-200 hover:bg-green-50" onClick={() => onAction('verify', doc)}>
              <CheckCircle2 className="h-3 w-3" /> Verify
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => onAction('reject', doc)}>
              <XCircle className="h-3 w-3" /> Reject
            </Button>
          </>
        )}
        {status === 'rejected' && (
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => onAction('upload', doc)}>
            <Upload className="h-3 w-3" /> Re-upload
          </Button>
        )}
        {doc.file_url && (
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => window.open(doc.file_url, '_blank')}>
            <Eye className="h-3.5 w-3.5" />
          </Button>
        )}
        {doc.requirement_type !== 'mandatory' && isMissing && (
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400" onClick={() => onAction('mark-na', doc)} title="Mark as N/A">
            <Ban className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Category Section
function CategorySection({ category, documents, onAction }) {
  const [expanded, setExpanded] = useState(true);
  const meta = CATEGORY_META[category] || { label: category, icon: FileText, color: 'text-slate-600', bg: 'bg-slate-50' };
  const Icon = meta.icon;
  const verified = documents.filter(d => d.current_status === 'verified').length;
  const missing = documents.filter(d => ['missing', 'pending_upload'].includes(d.current_status)).length;

  return (
    <Card className="shadow-sm border border-slate-200 overflow-hidden" data-testid={`category-${category}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-3 sm:p-4 ${meta.bg} hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-3">
          <Icon className={`h-5 w-5 ${meta.color}`} />
          <span className="font-bold text-sm text-slate-900">{meta.label}</span>
          <span className="text-xs text-slate-500">{documents.length} item{documents.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          {verified > 0 && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">{verified} verified</span>}
          {missing > 0 && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">{missing} missing</span>}
          {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        </div>
      </button>
      {expanded && (
        <div>
          {documents.map(doc => (
            <DocumentRow key={doc.id} doc={doc} onAction={onAction} />
          ))}
        </div>
      )}
    </Card>
  );
}

export default function ProjectSetup() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [accessRecords, setAccessRecords] = useState([]);
  const [terminology, setTerminology] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterReq, setFilterReq] = useState('all');
  const [activeTab, setActiveTab] = useState('documents');

  // Dialogs
  const [uploadDialog, setUploadDialog] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [accessDialog, setAccessDialog] = useState(false);
  const [accessForm, setAccessForm] = useState({ title: '', area_zone: '', access_type: 'full', effective_from: '', effective_to: '', restrictions_conditions: '', notes: '' });
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [addDocDialog, setAddDocDialog] = useState(false);
  const [newDocForm, setNewDocForm] = useState({ document_category: 'contract_documents', document_type: 'custom', title: '', description: '', requirement_type: 'optional' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const projRes = await api.get(`/projects/${projectId}`);
      setProject(projRes.data.project || projRes.data);

      const family = projRes.data.project?.contract_family || projRes.data.project?.contract_type || projRes.data.contract_family || projRes.data.contract_type || 'JBCC';
      try {
        const termRes = await api.documentControl.getTerminology(family === 'NEC' ? 'NEC3' : family);
        setTerminology(termRes.data.terminology);
      } catch {}

      // Try to get register, initialize if empty
      let docsRes = await api.documentControl.getRegister(projectId);
      if (docsRes.data.length === 0) {
        await api.documentControl.initialize(projectId);
        docsRes = await api.documentControl.getRegister(projectId);
      }
      setDocuments(docsRes.data);

      const readyRes = await api.documentControl.getReadiness(projectId);
      setReadiness(readyRes.data);

      const accessRes = await api.documentControl.getAccessRecords(projectId);
      setAccessRecords(accessRes.data);
    } catch (err) {
      console.error('Failed to load project setup:', err);
      toast.error('Failed to load project setup data');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const refreshReadiness = async () => {
    const r = await api.documentControl.getReadiness(projectId);
    setReadiness(r.data);
  };

  const handleAction = async (action, doc) => {
    if (action === 'upload') {
      setUploadDialog(doc);
    } else if (action === 'verify') {
      try {
        await api.documentControl.verifyRecord(projectId, doc.id);
        toast.success(`${doc.title} verified`);
        loadData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Verification failed');
      }
    } else if (action === 'reject') {
      setRejectDialog(doc);
      setRejectReason('');
    } else if (action === 'mark-na') {
      try {
        await api.documentControl.markNA(projectId, doc.id);
        toast.success(`${doc.title} marked as N/A`);
        loadData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed');
      }
    }
  };

  const handleFileUploaded = (results) => {
    // Called when drag-and-drop upload completes for a document record
    if (uploadDialog && results?.length > 0) {
      setUploadDialog(null);
      loadData();
    }
  };

  const handleContractDataExtracted = async (data) => {
    // Apply extracted data to the project
    try {
      const updatePayload = {};
      if (data.contract_family && data.contract_family !== 'OTHER') {
        updatePayload.contract_family = data.contract_family;
        updatePayload.contract_type = data.contract_family;
      }
      if (data.contract_form) updatePayload.contract_form = data.contract_form;
      if (data.start_date) updatePayload.start_date = data.start_date;
      if (data.completion_date) updatePayload.end_date = data.completion_date;
      if (data.contract_value) updatePayload.contract_value = parseFloat(data.contract_value);
      if (data.contractor_name) updatePayload.contractor_company = data.contractor_name;
      if (data.employer_name) updatePayload.client_name = data.employer_name;
      if (data.sectional_completion_applies != null) updatePayload.sectional_completion_applies = data.sectional_completion_applies;

      // Setup data for contract-specific fields
      const setupData = {};
      if (data.principal_agent_name) {
        if (data.contract_family?.startsWith('NEC')) {
          setupData.project_manager_name = data.principal_agent_name;
        } else if (data.contract_family === 'FIDIC') {
          setupData.engineer_name = data.principal_agent_name;
        } else {
          setupData.principal_agent_name = data.principal_agent_name;
        }
      }
      if (data.principal_agent_company) {
        if (data.contract_family?.startsWith('NEC')) {
          setupData.project_manager_company = data.principal_agent_company;
        } else if (data.contract_family === 'FIDIC') {
          setupData.engineer_company = data.principal_agent_company;
        } else {
          setupData.principal_agent_company = data.principal_agent_company;
        }
      }
      if (Object.keys(setupData).length > 0) {
        updatePayload.contract_setup_data = { ...(project?.contract_setup_data || {}), ...setupData };
      }

      if (Object.keys(updatePayload).length > 0) {
        await api.put(`/projects/${projectId}`, updatePayload);
      }
      loadData();
    } catch (err) {
      console.error('Failed to apply extracted data:', err);
      toast.error('Failed to apply some extracted fields');
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectDialog || !rejectReason.trim()) return;
    try {
      await api.documentControl.rejectRecord(projectId, rejectDialog.id, rejectReason);
      toast.success(`${rejectDialog.title} rejected`);
      setRejectDialog(null);
      loadData();
    } catch (err) {
      toast.error('Rejection failed');
    }
  };

  const handleAddAccess = async () => {
    try {
      await api.documentControl.addAccessRecord(projectId, accessForm);
      toast.success('Access record added');
      setAccessDialog(false);
      setAccessForm({ title: '', area_zone: '', access_type: 'full', effective_from: '', effective_to: '', restrictions_conditions: '', notes: '' });
      loadData();
    } catch (err) {
      toast.error('Failed to add access record');
    }
  };

  const handleDeleteAccess = async (id) => {
    try {
      await api.documentControl.deleteAccessRecord(projectId, id);
      toast.success('Access record deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleAddDoc = async () => {
    try {
      await api.documentControl.addRecord(projectId, newDocForm);
      toast.success('Document record added');
      setAddDocDialog(false);
      setNewDocForm({ document_category: 'contract_documents', document_type: 'custom', title: '', description: '', requirement_type: 'optional' });
      loadData();
    } catch (err) {
      toast.error('Failed to add record');
    }
  };

  const loadAuditLog = async () => {
    const res = await api.documentControl.getAuditLog(projectId);
    setAuditLog(res.data);
    setShowAudit(true);
  };

  // Filter documents
  const filtered = documents.filter(d => {
    if (searchQuery && !d.title.toLowerCase().includes(searchQuery.toLowerCase()) && !d.document_type.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterStatus !== 'all' && d.current_status !== filterStatus) return false;
    if (filterReq !== 'all' && d.requirement_type !== filterReq) return false;
    return true;
  });

  // Group by category
  const grouped = {};
  const catOrder = Object.keys(CATEGORY_META);
  for (const doc of filtered) {
    const cat = doc.document_category;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(doc);
  }
  const sortedCategories = catOrder.filter(c => grouped[c]?.length > 0);

  const contractFamily = project?.contract_family || project?.contract_type || 'JBCC';
  const contractFormLabel = project?.contract_form || contractFamily;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid="setup-loading">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
          <span className="ml-2 text-slate-500">Loading project setup...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="project-setup-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-8 w-8 p-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900" data-testid="setup-title">
              Project Setup & Document Control
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">{project?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs font-bold bg-slate-900 text-white border-slate-900">
            {contractFamily}
          </Badge>
          {project?.project_status && (
            <Badge variant="outline" className={`text-xs font-bold ${
              project.project_status === 'live' ? 'bg-green-100 text-green-700 border-green-300' :
              project.project_status === 'in_setup' ? 'bg-amber-100 text-amber-700 border-amber-300' :
              'bg-slate-100 text-slate-600 border-slate-300'
            }`}>
              {project.project_status?.replace('_', ' ').toUpperCase()}
            </Badge>
          )}
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={loadAuditLog}>
            <History className="h-3.5 w-3.5" /> Audit Log
          </Button>
          <ContractExtractor projectId={projectId} onDataExtracted={handleContractDataExtracted} />
        </div>
      </div>

      {/* Contract Profile Banner */}
      <Card className="bg-slate-900 text-white border-0 shadow-lg" data-testid="contract-banner">
        <CardContent className="p-4 sm:p-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Contract Family</p>
              <p className="font-bold text-sm">{contractFamily}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">{terminology.admin_role || 'Administrator'}</p>
              <p className="font-bold text-sm">{project?.contract_setup_data?.principal_agent_name || project?.contract_setup_data?.project_manager_name || project?.contract_setup_data?.engineer_name || 'Not set'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">{terminology.start_date_label || 'Start Date'}</p>
              <p className="font-bold text-sm">{project?.start_date || 'Not set'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">{terminology.end_date_label || 'End Date'}</p>
              <p className="font-bold text-sm">{project?.end_date || 'Not set'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Readiness Panel */}
      <ReadinessPanel readiness={readiness} onRefresh={refreshReadiness} />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200" data-testid="setup-tabs">
        {[
          { key: 'documents', label: 'Document Register', icon: FolderOpen },
          { key: 'access', label: 'Access / Possession', icon: MapPin, count: accessRecords.length },
        ].map(tab => {
          const TIcon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <TIcon className="h-4 w-4" />
              {tab.label}
              {tab.count != null && <span className="text-[10px] bg-slate-200 px-1.5 py-0.5 rounded-full">{tab.count}</span>}
            </button>
          );
        })}
      </div>

      {/* Document Register Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
                data-testid="doc-search"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36 h-9" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="missing">Missing</SelectItem>
                <SelectItem value="uploaded">Uploaded</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="not_applicable">N/A</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterReq} onValueChange={setFilterReq}>
              <SelectTrigger className="w-36 h-9" data-testid="filter-req">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="mandatory">Mandatory</SelectItem>
                <SelectItem value="conditional">Conditional</SelectItem>
                <SelectItem value="optional">Optional</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" className="h-9 text-xs gap-1" onClick={() => setAddDocDialog(true)} data-testid="add-doc-btn">
              <Plus className="h-3.5 w-3.5" /> Add Record
            </Button>
          </div>

          {/* Category Sections */}
          <div className="space-y-3">
            {sortedCategories.map(cat => (
              <CategorySection
                key={cat}
                category={cat}
                documents={grouped[cat]}
                onAction={handleAction}
              />
            ))}
            {sortedCategories.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No documents match your filters</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Access / Possession Tab */}
      {activeTab === 'access' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Manage multiple access and possession records for different areas, zones, and phases.
            </p>
            <Button size="sm" className="h-9 text-xs gap-1" onClick={() => setAccessDialog(true)} data-testid="add-access-btn">
              <Plus className="h-3.5 w-3.5" /> Add Access Record
            </Button>
          </div>

          {accessRecords.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="py-12 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                <p className="text-slate-500 text-sm">No access / possession records yet</p>
                <p className="text-slate-400 text-xs mt-1">Add records for site handover, area access, phased possession, etc.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {accessRecords.map(acc => (
                <Card key={acc.id} className="shadow-sm" data-testid={`access-record-${acc.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-slate-400">{acc.access_number}</span>
                          <span className="font-semibold text-sm text-slate-900">{acc.title}</span>
                          <Badge variant="outline" className={`text-[10px] ${
                            acc.status === 'active' ? 'bg-green-100 text-green-700 border-green-200' :
                            acc.status === 'expired' ? 'bg-red-100 text-red-700 border-red-200' :
                            acc.status === 'pending' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                            'bg-slate-100 text-slate-500 border-slate-200'
                          }`}>
                            {acc.status}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200">
                            {acc.access_type}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-500">
                          {acc.area_zone && <span>Area: {acc.area_zone}</span>}
                          {acc.effective_from && <span>From: {acc.effective_from}</span>}
                          {acc.effective_to && <span>To: {acc.effective_to}</span>}
                        </div>
                        {acc.restrictions_conditions && (
                          <p className="text-xs text-amber-600 mt-1">Restrictions: {acc.restrictions_conditions}</p>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeleteAccess(acc.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={!!uploadDialog} onOpenChange={() => setUploadDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">Upload Document</DialogTitle>
          <DialogDescription>{uploadDialog?.title}</DialogDescription>
          <div className="mt-2">
            <FileUploadZone
              projectId={projectId}
              category={uploadDialog?.document_category || 'general'}
              documentRecordId={uploadDialog?.id}
              onUploadComplete={handleFileUploaded}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.dwg,.zip,.txt"
              maxFiles={5}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle>Reject Document</DialogTitle>
          <DialogDescription>{rejectDialog?.title}</DialogDescription>
          <Textarea
            placeholder="Rejection reason..."
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            data-testid="reject-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectSubmit} disabled={!rejectReason.trim()} data-testid="reject-submit-btn">
              <XCircle className="h-4 w-4 mr-1" /> Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Access Record Dialog */}
      <Dialog open={accessDialog} onOpenChange={setAccessDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogTitle>Add Access / Possession Record</DialogTitle>
          <div className="grid gap-3 mt-2">
            <div>
              <label className="text-xs font-medium text-slate-700">Title *</label>
              <Input value={accessForm.title} onChange={e => setAccessForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Phase 1 Site Handover" data-testid="access-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Area / Zone / Level</label>
                <Input value={accessForm.area_zone} onChange={e => setAccessForm(f => ({...f, area_zone: e.target.value}))} placeholder="e.g. Block A Level 2" data-testid="access-area" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Access Type</label>
                <Select value={accessForm.access_type} onValueChange={v => setAccessForm(f => ({...f, access_type: v}))}>
                  <SelectTrigger data-testid="access-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['full','partial','restricted','temporary','phased','sectional','revoked'].map(t => (
                      <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Effective From</label>
                <Input type="date" value={accessForm.effective_from} onChange={e => setAccessForm(f => ({...f, effective_from: e.target.value}))} data-testid="access-from" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Effective To</label>
                <Input type="date" value={accessForm.effective_to} onChange={e => setAccessForm(f => ({...f, effective_to: e.target.value}))} data-testid="access-to" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Restrictions / Conditions</label>
              <Textarea value={accessForm.restrictions_conditions} onChange={e => setAccessForm(f => ({...f, restrictions_conditions: e.target.value}))} placeholder="Any access restrictions..." data-testid="access-restrictions" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Notes</label>
              <Textarea value={accessForm.notes} onChange={e => setAccessForm(f => ({...f, notes: e.target.value}))} placeholder="Additional notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessDialog(false)}>Cancel</Button>
            <Button onClick={handleAddAccess} disabled={!accessForm.title.trim()} data-testid="access-submit-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Custom Document Record Dialog */}
      <Dialog open={addDocDialog} onOpenChange={setAddDocDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle>Add Document Record</DialogTitle>
          <div className="grid gap-3 mt-2">
            <div>
              <label className="text-xs font-medium text-slate-700">Title *</label>
              <Input value={newDocForm.title} onChange={e => setNewDocForm(f => ({...f, title: e.target.value}))} placeholder="Document title" data-testid="new-doc-title" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Category</label>
              <Select value={newDocForm.document_category} onValueChange={v => setNewDocForm(f => ({...f, document_category: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Requirement Type</label>
              <Select value={newDocForm.requirement_type} onValueChange={v => setNewDocForm(f => ({...f, requirement_type: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mandatory">Mandatory</SelectItem>
                  <SelectItem value="conditional">Conditional</SelectItem>
                  <SelectItem value="optional">Optional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Description</label>
              <Textarea value={newDocForm.description} onChange={e => setNewDocForm(f => ({...f, description: e.target.value}))} placeholder="Optional description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDocDialog(false)}>Cancel</Button>
            <Button onClick={handleAddDoc} disabled={!newDocForm.title.trim()} data-testid="add-doc-submit-btn">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={showAudit} onOpenChange={setShowAudit}>
        <DialogContent className="sm:max-w-2xl max-h-[70vh] overflow-y-auto">
          <DialogTitle>Setup Audit Log</DialogTitle>
          <div className="space-y-2 mt-2">
            {auditLog.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No audit log entries yet</p>
            ) : auditLog.map((entry, i) => (
              <div key={entry.id || i} className="flex items-start gap-3 text-xs border-b border-slate-100 pb-2">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-900">{entry.action?.replace(/_/g, ' ')}</p>
                  <p className="text-slate-500">{entry.details}</p>
                  <p className="text-slate-400 mt-0.5">{entry.user_email} &middot; {new Date(entry.timestamp).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
